<script setup>
import { computed, nextTick, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import AssistantAPI from 'dashboard/api/assistant';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import CardLayout from 'dashboard/components-next/CardLayout.vue';
import { INBOX_TYPES, getInboxIconByType } from 'dashboard/helper/inbox';
import AssistantPageLayout from '../components/AssistantPageLayout.vue';
import AssistantStatusCard from '../components/AssistantStatusCard.vue';
import SectionHeader from '../components/SectionHeader.vue';
import AssistantLoadError from '../components/AssistantLoadError.vue';
import { usePersistentRef } from '../composables/useAssistantCache';

const { t } = useI18n();

const CHANNEL_KEYS = {
  [INBOX_TYPES.WEB]: 'WEB_WIDGET',
  [INBOX_TYPES.FB]: 'MESSENGER',
  [INBOX_TYPES.TWITTER]: 'TWITTER_PROFILE',
  [INBOX_TYPES.WHATSAPP]: 'WHATSAPP',
  [INBOX_TYPES.API]: 'API',
  [INBOX_TYPES.EMAIL]: 'EMAIL',
  [INBOX_TYPES.TELEGRAM]: 'TELEGRAM',
  [INBOX_TYPES.LINE]: 'LINE',
  [INBOX_TYPES.SMS]: 'SMS',
  [INBOX_TYPES.INSTAGRAM]: 'INSTAGRAM',
  [INBOX_TYPES.TIKTOK]: 'TIKTOK',
};

const channelLabel = inbox => {
  let key = CHANNEL_KEYS[inbox.channel_type];
  if (inbox.channel_type === INBOX_TYPES.TWILIO) {
    key = inbox.medium === 'whatsapp' ? 'WHATSAPP' : 'SMS';
  }
  return key ? t(`INBOX_MGMT.CHANNELS.${key}`) : inbox.channel_type;
};

const channelIcon = inbox =>
  getInboxIconByType(inbox.channel_type, inbox.medium, 'outline');

const settings = usePersistentRef('settings');
// Skeleton only on the first visit; later visits show cached data
// instantly while a background re-fetch keeps it fresh.
const isFetching = ref(!settings.value);
const loadError = ref(false);
const isSaving = ref(false);

const activeTab = ref(0);
const tabs = computed(() => [
  { label: t('ASSISTANT.SETTINGS.TABS.GENERAL') },
  { label: t('ASSISTANT.SETTINGS.TABS.CONVERSATION') },
  { label: t('ASSISTANT.SETTINGS.TABS.SMART') },
  { label: t('ASSISTANT.SETTINGS.TABS.INBOXES') },
]);
const tabRefs = ref([]);

// Arrow keys move between tabs (mirrored in RTL), as in a native tab list.
const onTabKeydown = event => {
  const isRtl = getComputedStyle(event.currentTarget).direction === 'rtl';
  const steps = { ArrowRight: isRtl ? -1 : 1, ArrowLeft: isRtl ? 1 : -1 };
  const last = tabs.value.length - 1;
  let next;
  if (event.key in steps) {
    next = (activeTab.value + steps[event.key] + last + 1) % (last + 1);
  } else if (event.key === 'Home') {
    next = 0;
  } else if (event.key === 'End') {
    next = last;
  } else {
    return;
  }
  event.preventDefault();
  activeTab.value = next;
  nextTick(() => tabRefs.value[next]?.focus());
};

const fetchSettings = async () => {
  isFetching.value = !settings.value;
  loadError.value = false;
  try {
    const { data } = await AssistantAPI.getSettings();
    settings.value = data;
  } catch (error) {
    loadError.value = true;
    useAlert(t('ASSISTANT.SETTINGS.LOAD_ERROR'));
  } finally {
    isFetching.value = false;
  }
};

const saveSettings = async () => {
  isSaving.value = true;
  try {
    const { data } = await AssistantAPI.updateSettings({
      enabled: settings.value.enabled,
      assistant_name: settings.value.assistant_name,
      business_name: settings.value.business_name,
      instructions: settings.value.instructions,
      welcome_message: settings.value.welcome_message,
      handoff_message: settings.value.handoff_message,
      handoff_keywords: settings.value.handoff_keywords,
      handoff_message_ar: settings.value.handoff_message_ar,
      repeat_limit: settings.value.repeat_limit,
      resolution_message: settings.value.resolution_message,
      feature_faq_learning: settings.value.feature_faq_learning,
      action_handoff: settings.value.action_handoff,
      action_resolve: settings.value.action_resolve,
      action_label: settings.value.action_label,
    });
    settings.value = data;
    useAlert(t('ASSISTANT.SETTINGS.SAVED'));
  } catch (error) {
    useAlert(t('ASSISTANT.SETTINGS.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const isTogglingEnabled = ref(false);

const toggleEnabled = async () => {
  isTogglingEnabled.value = true;
  try {
    const { data } = await AssistantAPI.updateSettings({
      enabled: !settings.value.enabled,
    });
    settings.value = data;
    useAlert(t('ASSISTANT.SETTINGS.SAVED'));
  } catch (error) {
    useAlert(t('ASSISTANT.SETTINGS.SAVE_ERROR'));
  } finally {
    isTogglingEnabled.value = false;
  }
};

const inboxes = usePersistentRef('inboxes', []);
const togglingInboxId = ref(null);

const connectedInboxCount = computed(
  () => inboxes.value.filter(inbox => inbox.connected).length
);

const usagePercent = computed(() => {
  if (!settings.value?.monthly_limit) return 0;
  return Math.min(
    100,
    Math.round(
      (settings.value.month_usage / settings.value.monthly_limit) * 100
    )
  );
});

const fetchInboxes = async () => {
  try {
    const { data } = await AssistantAPI.getInboxes();
    inboxes.value = data;
  } catch (error) {
    // the settings load error banner already covers a dead backend
  }
};

const toggleInbox = async inbox => {
  togglingInboxId.value = inbox.id;
  try {
    const { data } = await AssistantAPI.toggleInbox({
      inboxId: inbox.id,
      connected: !inbox.connected,
    });
    inbox.connected = data.connected;
  } catch (error) {
    useAlert(t('ASSISTANT.SETTINGS.INBOX_TOGGLE_ERROR'));
  } finally {
    togglingInboxId.value = null;
  }
};

onMounted(() => {
  fetchSettings();
  fetchInboxes();
});
</script>

<template>
  <AssistantPageLayout
    :header-title="t('ASSISTANT.SETTINGS.TITLE')"
    :description="t('ASSISTANT.SETTINGS.DESCRIPTION')"
    :is-fetching="isFetching"
    wide
  >
    <div
      v-if="settings"
      class="flex flex-col-reverse items-start w-full gap-6 xl:flex-row"
    >
      <form
        class="flex min-w-0 flex-1 flex-col w-full max-w-3xl gap-6 rounded-lg border border-n-weak bg-n-surface-1 p-4 sm:p-6"
        @submit.prevent="saveSettings"
      >
        <div
          role="tablist"
          class="flex max-w-full gap-4 overflow-x-auto border-b border-n-weak"
          :aria-label="t('ASSISTANT.SETTINGS.TITLE')"
          @keydown="onTabKeydown"
        >
          <button
            v-for="(tab, index) in tabs"
            :id="`assistant-settings-tab-${index}`"
            :key="tab.label"
            :ref="el => (tabRefs[index] = el)"
            type="button"
            role="tab"
            :aria-selected="activeTab === index"
            :aria-controls="`assistant-settings-panel-${index}`"
            :tabindex="activeTab === index ? 0 : -1"
            class="min-h-11 shrink-0 border-b-2 pb-3 pt-2 text-sm transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-n-blue-8"
            :class="
              activeTab === index
                ? 'border-n-brand font-semibold text-n-blue-11'
                : 'border-transparent text-n-slate-11 hover:text-n-slate-12'
            "
            @click="activeTab = index"
          >
            {{ tab.label }}
          </button>
        </div>

        <div
          v-show="activeTab === 0"
          id="assistant-settings-panel-0"
          role="tabpanel"
          aria-labelledby="assistant-settings-tab-0"
          class="flex flex-col gap-6"
        >
          <Input
            v-model="settings.assistant_name"
            :label="t('ASSISTANT.SETTINGS.NAME')"
            :placeholder="t('ASSISTANT.SETTINGS.NAME_PLACEHOLDER')"
          />

          <Input
            v-model="settings.business_name"
            :label="t('ASSISTANT.SETTINGS.BUSINESS_NAME')"
            :placeholder="t('ASSISTANT.SETTINGS.BUSINESS_NAME_PLACEHOLDER')"
          />

          <TextArea
            v-model="settings.instructions"
            :label="t('ASSISTANT.SETTINGS.INSTRUCTIONS')"
            :placeholder="t('ASSISTANT.SETTINGS.INSTRUCTIONS_PLACEHOLDER')"
            auto-height
            min-height="8rem"
            max-height="20rem"
          />

          <div class="flex flex-col gap-2 p-4 border rounded-md border-n-weak">
            <span class="text-sm font-medium text-n-slate-12">
              {{
                t('ASSISTANT.SETTINGS.USAGE_TITLE', {
                  plan: settings.plan_name,
                })
              }}
            </span>
            <span class="text-sm tabular-nums text-n-slate-11">
              {{
                t('ASSISTANT.SETTINGS.USAGE_COUNT', {
                  used: settings.month_usage,
                  limit: settings.monthly_limit,
                })
              }}
            </span>
            <div class="w-full h-2 overflow-hidden rounded-full bg-n-alpha-1">
              <div
                class="h-full rounded-full bg-n-brand"
                :style="{ width: `${usagePercent}%` }"
              />
            </div>
          </div>
        </div>

        <div
          v-show="activeTab === 1"
          id="assistant-settings-panel-1"
          role="tabpanel"
          aria-labelledby="assistant-settings-tab-1"
          class="flex flex-col gap-6"
        >
          <TextArea
            v-model="settings.welcome_message"
            :label="t('ASSISTANT.SETTINGS.WELCOME_MESSAGE')"
            auto-height
          />

          <div class="flex flex-col gap-4 p-4 border rounded-md border-n-weak">
            <div class="flex items-center justify-between w-full gap-4">
              <div class="flex flex-col gap-1">
                <span class="text-sm font-medium text-n-slate-12">
                  {{ t('ASSISTANT.SETTINGS.HANDOFF_ENABLED') }}
                </span>
                <span class="text-sm text-n-slate-11">
                  {{ t('ASSISTANT.SETTINGS.HANDOFF_ENABLED_DESCRIPTION') }}
                </span>
              </div>
              <Switch
                v-model="settings.action_handoff"
                :aria-label="t('ASSISTANT.SETTINGS.HANDOFF_ENABLED')"
              />
            </div>
            <TextArea
              v-model="settings.handoff_message"
              :label="t('ASSISTANT.SETTINGS.HANDOFF_MESSAGE')"
              :disabled="!settings.action_handoff"
              auto-height
            />
            <div class="flex flex-col gap-1">
              <TextArea
                v-model="settings.handoff_message_ar"
                :label="t('ASSISTANT.SETTINGS.HANDOFF_MESSAGE_AR')"
                :disabled="!settings.action_handoff"
                dir="rtl"
                auto-height
              />
              <span class="text-sm text-n-slate-11">
                {{ t('ASSISTANT.SETTINGS.HANDOFF_MESSAGE_AR_HELP') }}
              </span>
            </div>
            <Input
              v-model="settings.handoff_keywords"
              :label="t('ASSISTANT.SETTINGS.HANDOFF_KEYWORDS')"
              :placeholder="
                t('ASSISTANT.SETTINGS.HANDOFF_KEYWORDS_PLACEHOLDER')
              "
              :disabled="!settings.action_handoff"
            />
            <div class="flex flex-col gap-1">
              <Input
                v-model.number="settings.repeat_limit"
                type="number"
                min="0"
                :label="t('ASSISTANT.SETTINGS.REPEAT_LIMIT')"
              />
              <span class="text-sm text-n-slate-11">
                {{ t('ASSISTANT.SETTINGS.REPEAT_LIMIT_HELP') }}
              </span>
            </div>
          </div>

          <div class="flex flex-col gap-4 p-4 border rounded-md border-n-weak">
            <div class="flex items-center justify-between w-full gap-4">
              <div class="flex flex-col gap-1">
                <span class="text-sm font-medium text-n-slate-12">
                  {{ t('ASSISTANT.SETTINGS.RESOLVE_ENABLED') }}
                </span>
                <span class="text-sm text-n-slate-11">
                  {{ t('ASSISTANT.SETTINGS.RESOLVE_ENABLED_DESCRIPTION') }}
                </span>
              </div>
              <Switch
                v-model="settings.action_resolve"
                :aria-label="t('ASSISTANT.SETTINGS.RESOLVE_ENABLED')"
              />
            </div>
            <TextArea
              v-model="settings.resolution_message"
              :label="t('ASSISTANT.SETTINGS.RESOLUTION_MESSAGE')"
              :disabled="!settings.action_resolve"
              auto-height
            />
          </div>
        </div>

        <div
          v-show="activeTab === 2"
          id="assistant-settings-panel-2"
          role="tabpanel"
          aria-labelledby="assistant-settings-tab-2"
          class="flex flex-col gap-6"
        >
          <div class="flex items-center justify-between w-full gap-4">
            <div class="flex flex-col gap-1">
              <span class="text-sm font-medium text-n-slate-12">
                {{ t('ASSISTANT.SETTINGS.LABEL_ENABLED') }}
              </span>
              <span class="text-sm text-n-slate-11">
                {{ t('ASSISTANT.SETTINGS.LABEL_ENABLED_DESCRIPTION') }}
              </span>
            </div>
            <Switch
              v-model="settings.action_label"
              :aria-label="t('ASSISTANT.SETTINGS.LABEL_ENABLED')"
            />
          </div>

          <div class="flex items-center justify-between w-full gap-4">
            <div class="flex flex-col gap-1">
              <span class="text-sm font-medium text-n-slate-12">
                {{ t('ASSISTANT.SETTINGS.FAQ_LEARNING') }}
              </span>
              <span class="text-sm text-n-slate-11">
                {{ t('ASSISTANT.SETTINGS.FAQ_LEARNING_DESCRIPTION') }}
              </span>
            </div>
            <Switch
              v-model="settings.feature_faq_learning"
              :aria-label="t('ASSISTANT.SETTINGS.FAQ_LEARNING')"
            />
          </div>
        </div>

        <div
          v-show="activeTab === 3"
          id="assistant-settings-panel-3"
          role="tabpanel"
          aria-labelledby="assistant-settings-tab-3"
          class="flex flex-col gap-6"
        >
          <div class="flex flex-wrap items-start justify-between w-full gap-3">
            <SectionHeader
              :heading="t('ASSISTANT.SETTINGS.INBOXES_TITLE')"
              :description="t('ASSISTANT.SETTINGS.INBOXES_DESCRIPTION')"
            />
            <span
              v-if="inboxes.length"
              class="px-2 py-1 text-xs rounded-md shrink-0 whitespace-nowrap bg-n-alpha-1 text-n-slate-11"
            >
              {{
                t('ASSISTANT.SETTINGS.INBOX_COUNT', {
                  active: connectedInboxCount,
                  total: inboxes.length,
                })
              }}
            </span>
          </div>

          <p v-if="!inboxes.length" class="text-sm text-n-slate-11">
            {{ t('ASSISTANT.SETTINGS.INBOXES_EMPTY') }}
          </p>
          <div v-else class="flex flex-col gap-3">
            <CardLayout v-for="inbox in inboxes" :key="inbox.id" layout="row">
              <div class="flex items-center min-w-0 gap-3">
                <span
                  class="flex items-center justify-center rounded-lg size-9 shrink-0 bg-n-alpha-1 text-n-slate-11"
                >
                  <Icon :icon="channelIcon(inbox)" class="text-lg" />
                </span>
                <div class="flex flex-col min-w-0">
                  <span class="text-sm font-medium truncate text-n-slate-12">
                    {{ inbox.name }}
                  </span>
                  <span class="text-xs text-n-slate-11">
                    {{ channelLabel(inbox) }}
                  </span>
                </div>
              </div>
              <Switch
                :model-value="inbox.connected"
                :disabled="togglingInboxId === inbox.id"
                :aria-label="inbox.name"
                @update:model-value="toggleInbox(inbox)"
              />
            </CardLayout>
          </div>
        </div>

        <div
          v-show="activeTab !== 3"
          class="flex flex-wrap items-center gap-3 border-t border-n-weak pt-4"
        >
          <Button
            type="submit"
            :label="t('ASSISTANT.SETTINGS.SAVE')"
            :is-loading="isSaving"
            :disabled="isSaving"
            class="!min-h-11 !rounded-md"
          />
          <span class="text-sm text-n-slate-11">
            {{ t('ASSISTANT.SETTINGS.SAVE_HINT') }}
          </span>
        </div>
      </form>

      <aside class="w-full xl:w-[264px] xl:shrink-0 xl:sticky xl:top-0">
        <AssistantStatusCard
          :enabled="settings.enabled"
          :is-saving="isTogglingEnabled"
          @toggle="toggleEnabled"
        />
      </aside>
    </div>
    <AssistantLoadError
      v-else-if="loadError"
      :message="t('ASSISTANT.SETTINGS.LOAD_ERROR')"
      @retry="fetchSettings"
    />
  </AssistantPageLayout>
</template>
